import { useEffect, useRef, useMemo, useCallback } from 'react';
import { FullProject, ViewMode, PoolTab, WorkspaceType } from '../types';
import { pruneAbandonedInlineDraftsOnViewSwitch } from '../utils/dragGuardState';

export const GLOBAL_VIEW_IDS = [
  'dashboard',
  'metricsDashboard',
  'globalKanban',
  'communication',
  'impactEffort',
  'noDateTasks',
  'global',
  'globalCalendar',
  'aiAssistant',
  'analysis',
  'projectPool',
  'globalGantt',
];

export const PERSISTENT_PROJECT_VIEWS = [
  'kanban',
  'gantt',
  'list',
  'calendar',
  'projectContact',
  'timeline',
  'mindmap',
  'analysis',
  'projectDocuments',
];

interface UseViewNavigationPersistenceParams {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  currentProjectId: string;
  setCurrentProjectId: (id: string) => void;
  activeWorkspace: WorkspaceType;
  setActiveWorkspace: (ws: WorkspaceType) => void;
  activePoolTab: PoolTab;
  setActivePoolTab: (tab: PoolTab) => void;
  hiddenProjectIds: string[];
  setHiddenProjectIds: (ids: string[]) => void;
  showTaskDetails: boolean;
  setShowTaskDetails: (show: boolean) => void;
  setShowExitConfirm: (show: boolean) => void;
  syncProjectViews: boolean;
  selectedAnalysisProjectId: string;
  setSelectedAnalysisProjectId: (id: string) => void;
  allProjects: FullProject[];
  projects: FullProject[];
  setAllProjects?: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
}

export function useViewNavigationPersistence({
  viewMode,
  setViewMode,
  currentProjectId,
  setCurrentProjectId,
  activeWorkspace,
  setActiveWorkspace,
  activePoolTab,
  setActivePoolTab,
  hiddenProjectIds,
  setHiddenProjectIds,
  showTaskDetails,
  setShowTaskDetails,
  setShowExitConfirm,
  syncProjectViews,
  selectedAnalysisProjectId,
  setSelectedAnalysisProjectId,
  allProjects,
  projects,
  setAllProjects,
}: UseViewNavigationPersistenceParams) {
  const isNavigatingFromPopStateRef = useRef(false);
  const lastPushedUrlRef = useRef<string | null>(null);
  const prevViewModeRef = useRef<string>(viewMode);

  useEffect(() => {
    const prevView = prevViewModeRef.current;
    prevViewModeRef.current = viewMode;
    if (prevView && prevView !== viewMode && setAllProjects) {
      setAllProjects((prev) => pruneAbandonedInlineDraftsOnViewSwitch(prev, prevView, viewMode));
    }
  }, [viewMode, setAllProjects]);

  const syncStateFromUrl = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    const projectId = params.get('projectId');
    const workspace = params.get('workspace');
    const poolTab = params.get('poolTab');
    const hiddenStr = params.get('hiddenProjects');
    const detailsVal = params.get('details');

    // Only set popstate flag if we are parsing an existing URL state to prevent pushState during initial replaceState
    if (window.location.search) {
      isNavigatingFromPopStateRef.current = true;
    }

    setActiveWorkspace((workspace as WorkspaceType) || 'professional');
    setViewMode((view as ViewMode) || 'globalKanban');
    setCurrentProjectId(projectId || 'globalKanban');
    setActivePoolTab((poolTab as PoolTab) || 'activeProjects');

    if (hiddenStr !== null) {
      setHiddenProjectIds(hiddenStr ? hiddenStr.split(',') : []);
    } else {
      setHiddenProjectIds([]);
    }

    if (detailsVal !== null) {
      setShowTaskDetails(detailsVal === 'true');
    }
  }, [
    setViewMode,
    setActiveWorkspace,
    setCurrentProjectId,
    setActivePoolTab,
    setHiddenProjectIds,
    setShowTaskDetails,
  ]);

  // Seed exit barrier on mount to intercept back button exit attempts
  useEffect(() => {
    window.history.replaceState({ isExitBarrier: true }, '', window.location.href);
    window.history.pushState(null, '', window.location.href);
  }, []);

  useEffect(() => {
    syncStateFromUrl();

    const handlePopState = (event: PopStateEvent) => {
      if (event.state && event.state.isExitBarrier) {
        event.stopImmediatePropagation();
        window.history.forward();
        setShowExitConfirm(true);
        return;
      }

      syncStateFromUrl();
    };

    window.addEventListener('popstate', handlePopState, true);
    return () => {
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [syncStateFromUrl, setShowExitConfirm]);

  const hiddenProjectsDep = useMemo(() => hiddenProjectIds.join(','), [hiddenProjectIds]);

  useEffect(() => {
    // If this run is caused by popstate state restoration, consume flag and return early
    if (isNavigatingFromPopStateRef.current) {
      isNavigatingFromPopStateRef.current = false;
      return;
    }

    const newParams = new URLSearchParams(window.location.search);
    newParams.set('view', viewMode);
    newParams.set('workspace', activeWorkspace);
    if (currentProjectId) {
      newParams.set('projectId', currentProjectId);
    }
    if (viewMode === 'projectPool') {
      newParams.set('poolTab', activePoolTab);
    } else {
      newParams.delete('poolTab');
    }
    if (hiddenProjectsDep.length > 0) {
      newParams.set('hiddenProjects', hiddenProjectsDep);
    } else {
      newParams.delete('hiddenProjects');
    }
    newParams.set('details', String(showTaskDetails));

    const newQueryString = `?${newParams.toString()}`;
    const currentQueryString = window.location.search;

    if (lastPushedUrlRef.current !== newQueryString && newQueryString !== currentQueryString) {
      const newUrl = `${window.location.pathname}${newQueryString}`;
      lastPushedUrlRef.current = newQueryString;

      if (!currentQueryString) {
        window.history.replaceState(null, '', newUrl);
      } else {
        window.history.pushState(null, '', newUrl);
      }
    }
  }, [viewMode, currentProjectId, activeWorkspace, activePoolTab, hiddenProjectsDep, showTaskDetails]);

  const isProjectView = useMemo(
    () =>
      [
        'kanban',
        'gantt',
        'list',
        'timeline',
        'mindmap',
        'projectContact',
        'calendar',
        'projectDocuments',
      ].includes(viewMode),
    [viewMode]
  );

  const isConceptView = useMemo(
    () => ['concept', 'conceptBuilder', 'report'].includes(viewMode),
    [viewMode]
  );

  // Synchronize selectedAnalysisProjectId with last active project when entering analysis mode
  useEffect(() => {
    if (viewMode === 'analysis' && !selectedAnalysisProjectId) {
      const savedProjectId = localStorage.getItem('gantt_current_project_id');
      if (savedProjectId && allProjects.some(p => p.id === savedProjectId)) {
        setSelectedAnalysisProjectId(savedProjectId);
      } else if (projects.length > 0) {
        setSelectedAnalysisProjectId(projects[0].id);
      }
    }
  }, [viewMode, selectedAnalysisProjectId, allProjects, projects, setSelectedAnalysisProjectId]);

  const isStandardTaskView = useMemo(() => {
    const taskModes = [
      'kanban',
      'gantt',
      'list',
      'timeline',
      'mindmap',
      'calendar',
      'projectContact',
      'projectDocuments',
    ];
    const exclusionModes = [
      'globalKanban',
      'communication',
      'analysis',
      'impactEffort',
      'metricsDashboard',
      'dashboard',
      'globalCalendar',
      'projectPool',
      'aiAssistant',
      'globalGantt',
    ];
    return taskModes.includes(viewMode) && !exclusionModes.includes(viewMode);
  }, [viewMode]);

  // Restore the last view when switching projects
  const prevProjectIdForRestoreRef = useRef(currentProjectId);
  useEffect(() => {
    // Break the cascade: only run this effect if the project ID actually changed!
    if (prevProjectIdForRestoreRef.current === currentProjectId) return;
    prevProjectIdForRestoreRef.current = currentProjectId;

    // Guard: if viewMode itself is a global view (e.g. projectPool), never override it with a project-specific view.
    if (GLOBAL_VIEW_IDS.includes(viewMode)) return;
    if (currentProjectId && !GLOBAL_VIEW_IDS.includes(currentProjectId)) {
      if (syncProjectViews) {
        if (!PERSISTENT_PROJECT_VIEWS.includes(viewMode)) {
          const savedView = localStorage.getItem(`gwantt_last_view_${currentProjectId}`);
          if (savedView && PERSISTENT_PROJECT_VIEWS.includes(savedView)) {
            setViewMode(savedView as ViewMode);
          } else {
            setViewMode('gantt');
          }
        }
      } else {
        const savedView = localStorage.getItem(`gwantt_last_view_${currentProjectId}`);
        if (savedView && PERSISTENT_PROJECT_VIEWS.includes(savedView) && savedView !== viewMode) {
          setViewMode(savedView as ViewMode);
        }
      }
    }
  }, [currentProjectId, syncProjectViews, viewMode, setViewMode]);

  // Save the view when it changes within a project
  useEffect(() => {
    if (
      currentProjectId &&
      !GLOBAL_VIEW_IDS.includes(currentProjectId) &&
      PERSISTENT_PROJECT_VIEWS.includes(viewMode)
    ) {
      localStorage.setItem(`gwantt_last_view_${currentProjectId}`, viewMode);
    }
  }, [viewMode, currentProjectId]);

  return {
    isProjectView,
    isConceptView,
    isStandardTaskView,
  };
}
